// src/lib/priority.js
//
// The scheduling brain. Turns "when does the customer need it" into
// "when do WE have to start it" and "when must it be finished" —
// walking backwards over the shop's open days.
//
// Everything here is pure. No Firestore, no React. Easy to reason about.

// ─── Date helpers ─────────────────────────────────────────────────────────────
// Dates are handled as local 'YYYY-MM-DD' strings everywhere. Parsing at noon
// keeps us clear of DST/UTC off-by-one-day bugs.

export function todayStr(now = new Date()) {
  return ymd(now);
}

export function ymd(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function parseYmd(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12, 0, 0); // noon = DST-safe
}

export function addDays(s, n) {
  const d = parseYmd(s);
  if (!d) return null;
  d.setDate(d.getDate() + n);
  return ymd(d);
}

/** Whole days from a → b. Negative means b is in the past. */
export function daysBetween(a, b) {
  const da = parseYmd(a), db = parseYmd(b);
  if (!da || !db) return null;
  return Math.round((db - da) / 86400000);
}

export function weekdayOf(s) {
  const d = parseYmd(s);
  return d ? d.getDay() : null; // 0 = Sunday
}

export function fmtDay(s, { withYear = false } = {}) {
  const d = parseYmd(s);
  if (!d) return '—';
  return d.toLocaleDateString('en-CA', {
    weekday: 'short', month: 'short', day: 'numeric',
    ...(withYear && { year: 'numeric' }),
  });
}

/** "Today" / "Tomorrow" / "3 days ago" / "Sat, Aug 2" — for headline display. */
export function relativeDay(s, today = todayStr()) {
  const diff = daysBetween(today, s);
  if (diff === null) return '—';
  if (diff === 0)  return 'Today';
  if (diff === 1)  return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff < 0)    return `${-diff} days ago`;
  return fmtDay(s);
}

/**
 * Lower-case phrase for use *alongside* a date: "Sat, Aug 1 (in 6 days)".
 * Returns null when the date is far enough out that a phrase adds nothing —
 * callers should then print nothing rather than restating the date.
 */
export function relativePhrase(s, today = todayStr()) {
  const diff = daysBetween(today, s);
  if (diff === null) return null;
  if (diff === 0)  return 'today';
  if (diff === 1)  return 'tomorrow';
  if (diff === -1) return 'yesterday';
  if (diff < 0)    return `${-diff} days ago`;
  if (diff <= 14)  return `in ${diff} days`;
  return null;
}

// ─── Shop workflow config ─────────────────────────────────────────────────────

export const DEFAULT_WORKFLOW = {
  closedDays: [0],          // 0=Sun … 6=Sat. Rebecca's is closed Sunday.
  baseLeadDays: 1,          // everything gets made at least one open day early
  bigOrderThreshold: 200,   // $200+ …
  bigOrderLeadDays: 2,      // … start 2 open days out
  hugeOrderThreshold: 500,  // $500+ …
  hugeOrderLeadDays: 3,     // … start 3 open days out
  outOfTownLeadDays: 2,     // flights need checking 2 days ahead
  airportCutoffMins: 90,    // be at the airport this long before departure
};

export function workflowOf(shop) {
  return { ...DEFAULT_WORKFLOW, ...(shop?.workflow || {}) };
}

const isClosed = (dateStr, wf) => (wf.closedDays || []).includes(weekdayOf(dateStr));

/** Walk back `n` OPEN days from dateStr. n=1 on a Monday lands on Saturday. */
export function openDaysBefore(dateStr, n, wf) {
  let cur = dateStr;
  if (!cur) return null;
  let stepped = 0;
  let guard = 0;
  while (stepped < n && guard++ < 400) {
    cur = addDays(cur, -1);
    if (!isClosed(cur, wf)) stepped++;
  }
  return cur;
}

/** Count open days from a → b (exclusive of a, inclusive of b). */
export function openDaysBetween(a, b, wf) {
  const total = daysBetween(a, b);
  if (total === null) return null;
  const dir = total >= 0 ? 1 : -1;
  let count = 0, cur = a;
  for (let i = 0; i < Math.abs(total); i++) {
    cur = addDays(cur, dir);
    if (!isClosed(cur, wf)) count++;
  }
  return count * dir;
}

// ─── Reading a needed-date off any order, old or new ──────────────────────────
// Legacy orders wrote the date into three different places (and carry-outs
// wrote nothing at all). This normalises all of them so the board works on
// existing data without a migration.

export function neededDateOf(order) {
  return (
    order?.fulfillment?.neededDate ||
    order?.delivery?.date ||
    order?.pickupDate ||
    null
  );
}

export function neededTimeOf(order) {
  const t = order?.fulfillment?.neededTime || order?.delivery?.time || order?.pickupTime;
  return !t || t === 'None' ? '' : t;
}

const LEGACY_DESIGN = { Pending: 'not_started', 'In Progress': 'in_progress', Complete: 'done' };

export function designStatusOf(order) {
  if (order?.fulfillment?.designStatus) return order.fulfillment.designStatus;
  const legacy = order?.delivery?.designStatus;
  return LEGACY_DESIGN[legacy] || 'not_started';
}

/** Has it left the building (delivered / picked up / dropped at airport)? */
export function isHandedOff(order) {
  if (order?.fulfillment?.handedOff) return true;
  return order?.delivery?.status === 'Delivered';
}

export function isOutOfTown(order) {
  return !!order?.fulfillment?.outOfTown;
}

// ─── The main computation ─────────────────────────────────────────────────────

export const URGENCY = {
  late:      { rank: 0, label: 'LATE',        tone: 'red'   },
  // Flights outrank a normal make-today: a plane leaves at a fixed minute,
  // whereas an ordinary order just has to be finished before close.
  flight:    { rank: 1, label: 'FLIGHT',      tone: 'amber' },
  today:     { rank: 2, label: 'MAKE TODAY',  tone: 'red'   },
  start:     { rank: 3, label: 'START NOW',   tone: 'amber' },
  soon:      { rank: 4, label: 'COMING UP',   tone: 'blue'  },
  later:     { rank: 5, label: 'SCHEDULED',   tone: 'gray'  },
  nodate:    { rank: 6, label: 'NO DATE',     tone: 'gray'  },
  done:      { rank: 7, label: 'MADE',        tone: 'green' },
};

/**
 * Given an order + shop workflow, work out when it has to be started and
 * finished, why it's a priority, and how loud to shout about it.
 */
export function schedule(order, wf, today = todayStr()) {
  const neededDate  = neededDateOf(order);
  const neededTime  = neededTimeOf(order);
  const design      = designStatusOf(order);
  const handedOff   = isHandedOff(order);
  const outOfTown   = isOutOfTown(order);
  const total       = order?.total || 0;
  const flight      = order?.fulfillment?.flight || null;

  // Why is this a priority? Each reason can push the lead time out.
  const reasons = [];
  let leadDays = wf.baseLeadDays;

  if (total >= wf.hugeOrderThreshold) {
    leadDays = Math.max(leadDays, wf.hugeOrderLeadDays);
    reasons.push({ key: 'huge', label: `$${Math.round(total)} order`, tone: 'red' });
  } else if (total >= wf.bigOrderThreshold) {
    leadDays = Math.max(leadDays, wf.bigOrderLeadDays);
    reasons.push({ key: 'big', label: `$${Math.round(total)} order`, tone: 'amber' });
  }

  if (outOfTown) {
    leadDays = Math.max(leadDays, wf.outOfTownLeadDays);
    reasons.push({ key: 'outoftown', label: 'Out of town — flight', tone: 'amber' });
  }

  if (order?.recipient?.occasion === 'Funeral' || order?.recipient?.occasion === 'Sympathy') {
    reasons.push({ key: 'sympathy', label: order.recipient.occasion, tone: 'blue' });
  }

  // Finish the day before it's needed; start `leadDays` open days before.
  const finishBy = neededDate ? openDaysBefore(neededDate, wf.baseLeadDays, wf) : null;
  const startBy  = neededDate ? openDaysBefore(neededDate, leadDays,        wf) : null;

  // Airport drop-off deadline: flight departure minus the cutoff.
  let dropOffBy = null;
  if (outOfTown && flight?.departTime) {
    dropOffBy = minusMinutes(flight.departTime, wf.airportCutoffMins);
  }
  const flightDate = outOfTown ? (flight?.date || neededDate) : null;

  // Urgency — first matching rule wins.
  let urgency;
  if (design === 'done' || handedOff) {
    urgency = 'done';
  } else if (!neededDate) {
    urgency = 'nodate';
  } else if (finishBy && daysBetween(today, finishBy) < 0) {
    urgency = 'late';                                   // should already be made
  } else if (flightDate && daysBetween(today, flightDate) <= 1) {
    // Checked before the generic make-today rule so the flight section — and
    // its airport drop-off time — is what actually gets shown.
    urgency = 'flight';                                 // flies today or tomorrow
  } else if (finishBy === today || neededDate === today) {
    urgency = 'today';
  } else if (startBy && daysBetween(today, startBy) <= 0) {
    urgency = 'start';                                  // inside its lead window
  } else if (daysBetween(today, neededDate) <= 3) {
    urgency = 'soon';
  } else {
    urgency = 'later';
  }

  // Anything someone has physically started stays on the board until it's
  // marked made. Otherwise a half-built arrangement for next week drops out
  // of view and nobody is tracking it — the exact failure the board exists
  // to prevent. Never quietens an order that's already louder than this.
  if (design === 'in_progress' && URGENCY[urgency].rank > URGENCY.start.rank) {
    urgency = 'start';
  }

  return {
    neededDate, neededTime, finishBy, startBy, leadDays,
    design, handedOff, outOfTown, flight, flightDate, dropOffBy,
    reasons, urgency,
    ...URGENCY[urgency],
    daysUntilNeeded: neededDate ? daysBetween(today, neededDate) : null,
    /** Sort key: loudest first, then biggest ticket, then soonest. */
    sortKey: [URGENCY[urgency].rank, -total, neededDate || '9999-12-31'],
  };
}

function minusMinutes(hhmm, mins) {
  const [h, m] = String(hhmm).split(':').map(Number);
  if (isNaN(h)) return null;
  let t = h * 60 + (m || 0) - mins;
  if (t < 0) t += 1440;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

export function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = String(hhmm).split(':').map(Number);
  if (isNaN(h)) return String(hhmm);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12  = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
}

/** Orders that should never appear on the workboard. */
export function isActiveOrder(order) {
  return !['void', 'draft'].includes(order?.status);
}

/**
 * Decorate + sort a list of orders for the workboard.
 * Returns [{ ...order, sched }] sorted loudest-first.
 */
export function buildWorkboard(orders, shop, today = todayStr()) {
  const wf = workflowOf(shop);
  return orders
    .filter(isActiveOrder)
    .map(o => ({ ...o, sched: schedule(o, wf, today) }))
    .sort((a, b) => cmpKey(a.sched.sortKey, b.sched.sortKey));
}

function cmpKey(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] < b[i]) return -1;
    if (a[i] > b[i]) return 1;
  }
  return 0;
}
